import {
  Carousel,
  CarouselAnnouncerFunction,
  CarouselCard,
  CarouselNav,
  CarouselNavButton,
  CarouselNavContainer,
  CarouselSlider,
  CarouselViewport,
  makeStyles,
} from "@fluentui/react-components";
import { useMemo, useState } from "react";
import { $cloud20 } from "../../../cssjs/colors";
import { FlightCardTypes } from "../../../enum/flights/FlightEnum";
import { KPISettingsIDs } from "../../../enum/FlightsEnum";
import { useApiCall } from "../../../hooks/useApiCall";
import runwayDelayData from "../../../mock/flights/runway-delay-mock.json";
import {
  AIRPORT_CODE_PARAMETER,
  kpiTypes,
} from "../../../utils/common/Constants";
import { getFlightKPICarouselIndex } from "../../../utils/flights/FlightDetailHelperFunctions";
import FlightKPICards from "./FlightKpiCards";

const getAnnouncement: CarouselAnnouncerFunction = (
  index: number,
  totalSlides: number
) => {
  return `Carousel slide ${index + 1} of ${totalSlides}`;
};

const hopSettingsURL = process.env.REACT_APP_API_GET_HOP_SETTINGS;
const baseURL = process.env.REACT_APP_API_BASE_URL;

interface IFlightKPIsProps {
  selectedSection: FlightCardTypes;
  airport: string;
  kpiConfigData: any;
  onSectionChange: (newSection: FlightCardTypes) => void;
}

interface CardConfig {
  cardName: FlightCardTypes;
  showSettings: boolean;
  showInformation: boolean;
  kpiSettingsId: KPISettingsIDs;
  kpiData?: any;
}

interface CarouselPageConfig {
  cards: CardConfig[];
}

const CARDS_PER_PAGE = 2;

const chunkIntoPages = (cards: CardConfig[]): CarouselPageConfig[] => {
  const pages: CarouselPageConfig[] = [];
  for (let i = 0; i < cards.length; i += CARDS_PER_PAGE) {
    pages.push({ cards: cards.slice(i, i + CARDS_PER_PAGE) });
  }
  return pages;
};

const commonCards: CardConfig[] = [
  {
    cardName: FlightCardTypes.OTP,
    showSettings: true,
    showInformation: true,
    kpiSettingsId: KPISettingsIDs.ON_TIME_PERFORMANCE,
  },
  {
    cardName: FlightCardTypes.SLOT_ADHERENCE,
    showSettings: true,
    showInformation: true,
    kpiSettingsId: KPISettingsIDs.SLOT_ADHERENCE,
  },
  {
    cardName: FlightCardTypes.TURNAROUND_SUFFICIENCY,
    showSettings: true,
    showInformation: true,
    kpiSettingsId: KPISettingsIDs.TURNAROUND_SUFFICIENCY,
  },
  {
    cardName: FlightCardTypes.ADDITIONAL_DELAY,
    showSettings: true,
    showInformation: true,
    kpiSettingsId: KPISettingsIDs.ADDITIONAL_DELAY,
  },
  {
    cardName: FlightCardTypes.REGULATED_FLIGHT,
    showSettings: true,
    showInformation: true,
    kpiSettingsId: KPISettingsIDs.REGULATED_FLIGHT,
  },
];

const xyzCards: CardConfig[] = [
  ...commonCards,
  {
    cardName: FlightCardTypes.RUNWAY_DELAYS,
    showSettings: false,
    showInformation: false,
    kpiSettingsId: KPISettingsIDs.RUNWAY_DELAY,
    kpiData: runwayDelayData,
  },
];

const yqbCards: CardConfig[] = commonCards.filter(
  (card: CardConfig) => card.cardName !== FlightCardTypes.SLOT_ADHERENCE
);

const getCarouselPages = (airport: string): CarouselPageConfig[] => {
  switch (airport) {
    case "XYZ":
      return chunkIntoPages(xyzCards);
    case "YQB":
      return chunkIntoPages(yqbCards);
    default:
      return chunkIntoPages(commonCards);
  }
};

const FlightKPIs: React.FC<IFlightKPIsProps> = ({
  selectedSection,
  airport,
  kpiConfigData,
  onSectionChange,
}) => {
  const styles = useStyles();
  const [activeIndex, setActiveIndex] = useState(
    getFlightKPICarouselIndex(airport, selectedSection)
  );

  const hopSettingsUrls = useMemo(
    () =>
      kpiTypes.map((kpiType) =>
        `${baseURL}${hopSettingsURL}`
          .replace(AIRPORT_CODE_PARAMETER, airport)
          .replace("{kpiType}", kpiType)
      ),
    [baseURL, hopSettingsURL, airport]
  );

  const hopSettingsDataList = [
    useApiCall<any>({
      url: hopSettingsUrls[0],
      dependencies: [airport, kpiTypes[0]],
    }).data,
    useApiCall<any>({
      url: hopSettingsUrls[1],
      dependencies: [airport, kpiTypes[1]],
    }).data,
    useApiCall<any>({
      url: hopSettingsUrls[2],
      dependencies: [airport, kpiTypes[2]],
    }).data,
    useApiCall<any>({
      url: hopSettingsUrls[3],
      dependencies: [airport, kpiTypes[3]],
    }).data,
  ].map((data, idx) => ({ kpiType: kpiTypes[idx], data }));

  const carouselPages = useMemo(() => getCarouselPages(airport), [airport]);

  const corouselContent = (
    <Carousel
      groupSize={1}
      align="center"
      activeIndex={activeIndex}
      announcement={getAnnouncement}
      onActiveIndexChange={(_e, data) => setActiveIndex(data.index)}
    >
      <CarouselViewport>
        <CarouselSlider>
          {carouselPages.map((page, pageIndex) => (
            <CarouselCard key={`page${pageIndex + 1}`} className={styles.card}>
              <div className={styles.cardInfo}>
                {page.cards.map((card) => (
                  <FlightKPICards
                    key={card.cardName}
                    selectedSection={selectedSection}
                    onSectionChange={onSectionChange}
                    airport={airport}
                    cardName={card.cardName}
                    showSettings={card.showSettings}
                    showInformation={card.showInformation}
                    kpiConfigData={kpiConfigData[card.kpiSettingsId]}
                    hopSettingsDataList={hopSettingsDataList}
                    {...(card.kpiData && { kpiData: card.kpiData })}
                  />
                ))}
              </div>
            </CarouselCard>
          ))}
        </CarouselSlider>
      </CarouselViewport>
      <div className={styles.buttonContainer}>
        <CarouselNavContainer
          layout="overlay-expanded"
          next={{ "aria-label": "go to next" }}
          prev={{ "aria-label": "go to prev" }}
        >
          <CarouselNav className={styles.carouselButton}>
            {(index) => (
              <CarouselNavButton aria-label={`Carousel Nav Button ${index}`} />
            )}
          </CarouselNav>
        </CarouselNavContainer>
      </div>
    </Carousel>
  );

  return (
    <div
      style={{ cursor: "pointer", display: "flex" }}
      className={`${styles.kpiCardContainer}`}
      data-testid="flight360-kpi"
    >
      {corouselContent}
    </div>
  );
};

export default FlightKPIs;

const useStyles = makeStyles({
  kpiCardContainer: {
    width: "clamp(500px, 50%, max(50%, 500px))",
    height: "330px",
    border: `2px solid #ccc`,
    borderRadius: "8px",
    padding: "10px 0px 10px 0px",
    margin: "10px 0px 10px 0px",
    display: "flex",
    flexWrap: "wrap",
    background: $cloud20,
  },

  buttonContainer: {
    "& [aria-label='go to prev']": {
      top: "46%",
      left: "-3px !important",
    },
    "& [aria-label='go to next']": {
      top: "46%",
      right: "-3px !important",
    },
  },
  carouselButton: {
    "&.fui-CarouselNav": {
      top: "0px",
    },
  },
  card: {
    boxSizing: "border-box",
    width: "100%",
  },
  cardInfo: {
    display: "flex",
    flexFlow: "row wrap",
    marginLeft: "30px",
  },
});
